import React, { useEffect, useRef, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

type SparklineProps = {
  /** Raw daily values over the chart window. A null entry is a gap in the metric's data and is
   *  skipped rather than drawn as zero. */
  values: ReadonlyArray<number | null | undefined>;
  height?: number;
  /** Trailing rolling-mean window applied to `values` before drawing (default 7, a 7-day rolling
   *  mean: daily series alone shows weekly seasonality as scribble). Pass 1 for no smoothing, e.g.
   *  when `values` is already a week/month-grain series, or a ratio series the caller has already
   *  smoothed correctly upstream (see `metricSparklineSeries` in `MetricStrip.tsx`, which computes
   *  a rolling ratio of summed numerator over summed denominator rather than averaging the ratio). */
  smoothWindow?: number;
};

const DEFAULT_WIDTH = 160;
const PAD_X = 4;
const PAD_Y = 4;
const LAST_POINT_RADIUS = 3;
const DEFAULT_SMOOTH_WINDOW = 7;

export function sparklineMode(pointCount: number): 'empty' | 'endpoint' | 'line' {
  return pointCount === 0 ? 'empty' : pointCount === 1 ? 'endpoint' : 'line';
}

/**
 * Trailing rolling mean of `values` (window ending at each point), ignoring nulls within the
 * window rather than treating them as zero. `window <= 1` is a no-op pass-through (still coercing
 * non-finite entries to null), which is how a week/month-grain series, or a series a caller has
 * already smoothed itself, opts out of further smoothing.
 */
export function rollingMean(values: ReadonlyArray<number | null | undefined>, window: number): Array<number | null> {
  const clean = values.map((value) => (typeof value === 'number' && Number.isFinite(value) ? value : null));
  if (window <= 1) {
    return clean;
  }
  return clean.map((_, index) => {
    const start = Math.max(0, index - window + 1);
    const windowValues = clean.slice(start, index + 1).filter((value): value is number => value !== null);
    return windowValues.length ? windowValues.reduce((sum, value) => sum + value, 0) / windowValues.length : null;
  });
}

/**
 * The metric strip's 32px sparkline. Pure SVG rather than an ECharts instance: at this size a chart
 * engine is pure overhead. Coordinates are built in real measured pixels (via ResizeObserver), not
 * an abstract viewBox stretched with `preserveAspectRatio="none"`, because that would scale the
 * 1.5px stroke and the r=3 last-point circle unevenly on the x axis and turn the dot into an
 * ellipse. A straight polyline through the (rolling-mean-smoothed) points is what "never scribbly"
 * means here: no curve-fitting smoothing is applied on top of that.
 */
export default function Sparkline({ values, height = 32, smoothWindow = DEFAULT_SMOOTH_WINDOW }: SparklineProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next) {
        setWidth(next);
      }
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  const smoothed = rollingMean(values, smoothWindow);
  const points = smoothed
    .map((value, index) => ({ value, index }))
    .filter(
      (entry): entry is { value: number; index: number } =>
        typeof entry.value === 'number' && Number.isFinite(entry.value),
    );

  const mode = sparklineMode(points.length);
  if (mode === 'empty') {
    return <Box ref={hostRef} sx={{ width: '100%', height }} />;
  }

  if (mode === 'endpoint') {
    return (
      <Box ref={hostRef} sx={{ width: '100%', height, lineHeight: 0 }}>
        <svg aria-hidden focusable="false" height={height} viewBox={`0 0 ${width} ${height}`} width={width}>
          <circle
            cx={Math.max(width - PAD_X - LAST_POINT_RADIUS, LAST_POINT_RADIUS)}
            cy={height / 2}
            fill={tokens.surface}
            r={LAST_POINT_RADIUS}
            stroke={tokens.accent}
            strokeWidth={1.5}
          />
        </svg>
      </Box>
    );
  }

  const min = Math.min(...points.map((point) => point.value));
  const max = Math.max(...points.map((point) => point.value));
  const span = max - min;
  const usableWidth = Math.max(width - PAD_X * 2, 0);
  const usableHeight = Math.max(height - PAD_Y * 2, 0);
  const stepX = points.length > 1 ? usableWidth / (points.length - 1) : 0;

  const coords = points.map((point, index) => ({
    x: PAD_X + stepX * index,
    y: span === 0 ? height / 2 : PAD_Y + usableHeight * (1 - (point.value - min) / span),
  }));

  const path = coords
    .map((coord, index) => `${index === 0 ? 'M' : 'L'}${coord.x.toFixed(2)},${coord.y.toFixed(2)}`)
    .join(' ');
  const last = coords[coords.length - 1];

  return (
    <Box ref={hostRef} sx={{ width: '100%', height, lineHeight: 0 }}>
      <svg aria-hidden focusable="false" height={height} viewBox={`0 0 ${width} ${height}`} width={width}>
        <path
          d={path}
          fill="none"
          stroke={tokens.accent}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
        />
        <circle
          cx={last.x}
          cy={last.y}
          fill={tokens.surface}
          r={LAST_POINT_RADIUS}
          stroke={tokens.accent}
          strokeWidth={1.5}
        />
      </svg>
    </Box>
  );
}
